import EnemyEntity from '../entities/EnemyEntity';
import { PoolManager } from '../managers/PoolManager';
import { EventBus } from '../core/EventBus';
import { ISceneAdapter } from '../engine/ISceneAdapter';
import { AI_REGISTRY } from '../systems/ai/EnemyAI';
import { ContentRegistry, loadContentRegistry } from '../data';
import { EnemyResolvedStats } from '../systems/EnemyScalingResolver';
import { createLogger } from '../utils/Logger';
import { usesFallbackTexture, VisualTextureResolver } from '../utils/VisualTextureResolver';
import { applyCharacterDisplaySize } from '../utils/CharacterDisplaySizing';
import { BOSS_ENCOUNTER_METADATA_KEY } from '../systems/BossEventPayloads';

export interface EnemyCreationOverrides {
  readonly resolvedStats?: EnemyResolvedStats;
  readonly scalingMetadata?: {
    readonly waveIndex: number;
    readonly elapsedWaveTime: number;
    readonly modifierIds: readonly string[];
  };
}

/**
 * Класс фабрики врагов
 * Отвечает за создание экземпляров врагов с различными характеристиками
 */
export class EnemyFactory {
  private eventBus: EventBus;
  private readonly logger = createLogger('EnemyFactory');

  /**
   * Создает фабрику врагов
   * @param poolManager Менеджер пулов объектов
   * @param sceneAdapter Адаптер сцены
   */
  constructor(
    private poolManager: PoolManager,
    private sceneAdapter: ISceneAdapter,
    private contentRegistry: ContentRegistry = loadContentRegistry(),
    private readonly visualTextureResolver?: VisualTextureResolver
  ) {
    this.eventBus = EventBus.getInstance();
  }

  /**
   * Создает врага указанного типа
   * @param type Тип врага (id из конфигурации)
   * @param x Координата X для позиционирования
   * @param y Координата Y для позиционирования
   * @param overrides Optional resolved scaling stats/metadata.
   * @returns Экземпляр EnemyEntity или null в случае ошибки
   */
  public create(type: string, x: number, y: number, overrides?: EnemyCreationOverrides): EnemyEntity | null {
    const config = this.contentRegistry.getEnemy(type);
    if (!config) {
      this.logger.error('Enemy configuration not found', { enemyId: type });
      return null;
    }

    const baseStats: EnemyResolvedStats = {
      health: config.stats.hp,
      maxHealth: config.stats.hp,
      speed: config.stats.speed,
      damage: config.stats.damage,
      xpReward: config.stats.xpReward
    };
    const resolvedStats = overrides?.resolvedStats ?? baseStats;
    const scalingApplied = overrides?.resolvedStats !== undefined;

    // Create sprite using the sprite key from config
    const enemyGroup = this.poolManager.getGroup('enemy');
    if (!enemyGroup) {
      this.logger.error('Enemy pool group not found');
      return null;
    }
    const texture = this.visualTextureResolver?.resolve(config.sprite, 'enemy') ?? {
      requestedKey: config.sprite,
      resolvedKey: config.sprite,
      source: 'direct' as const,
      fallbackUsed: false
    };
    const sprite = enemyGroup.get(x, y, texture.resolvedKey);
    if (!sprite) return null;
    this.poolManager.resetVisual(sprite);
    if (sprite.body) sprite.body.enable = true;
    sprite.setData('burn', undefined);
    sprite.setData('slow', undefined);
    sprite.setData('modifierIds', overrides?.scalingMetadata?.modifierIds ?? []);

    // Создаем врага с использованием адаптера сцены
    const enemy = new EnemyEntity(
      this.sceneAdapter,
      x,
      y,
      sprite, // Pass the created sprite object
      resolvedStats.maxHealth,
      resolvedStats.speed
    );

    // Apply scaling / tint if defined
    if (sprite) {
      sprite.setData(BOSS_ENCOUNTER_METADATA_KEY, undefined);
      if (typeof sprite.setTexture === 'function') {
        sprite.setTexture(texture.resolvedKey);
      }

      const displaySize = applyCharacterDisplaySize(sprite, texture.resolvedKey, 'enemy', config.scale || 1);

      // Store ID and Max Health for CollisionSystem/UI
      sprite.setData('id', config.id);
      sprite.setData('health', resolvedStats.health);
      sprite.setData('maxHealth', resolvedStats.maxHealth);
      sprite.setData('speed', resolvedStats.speed);
      sprite.setData('damage', resolvedStats.damage);
      sprite.setData('xpReward', resolvedStats.xpReward);
      sprite.setData('visualTextureKey', texture.resolvedKey);
      sprite.setData('contentSpriteKey', config.sprite);
      sprite.setData('visualDisplayWidth', displaySize.width);
      sprite.setData('visualDisplayHeight', displaySize.height);
      sprite.setData('aiPattern', config.aiPattern);
      sprite.setData('entity', enemy); // Link back to entity for update loop

      if (config.color !== undefined && usesFallbackTexture(texture)) {
        sprite.setTint(config.color);
      } else {
        sprite.clearTint();
      }
    }

    // Setup AI
    const AICtor = AI_REGISTRY[config.aiPattern];
    if (AICtor) {
      const ai = new AICtor(config.behaviorParams);
      enemy.setAI(ai);
    } else {
      this.logger.warn('AI pattern not found', { aiPattern: config.aiPattern, enemyId: type });
    }

    // Публикуем событие о создании врага
    this.eventBus.publish({
      type: 'enemy:created',
      data: {
        enemy: enemy,
        type: config.id,
        position: { x, y },
        xpReward: resolvedStats.xpReward,
        scalingApplied,
        baseStats,
        resolvedStats,
        scalingMetadata: overrides?.scalingMetadata
      }
    });

    return enemy;
  }
}

export default EnemyFactory;
